import Auth from 'dashboard/api/auth';

const REFRESH_BUFFER_SECONDS = 60;

/**
 * Ensures a short-lived LangGraph session cookie (HttpOnly) is present.
 * Optionally returns a JWT body token when COPILOT_JWT_IN_BODY is enabled (local Bearer).
 * Never persists the token in IndexedDB / localStorage.
 */
export function createCopilotTokenClient(accountId) {
  let cachedToken = null;
  let cachedExpiresAt = 0;
  let inflight = null;

  async function fetchSession() {
    const response = await fetch(
      `/api/v1/accounts/${accountId}/copilot/token`,
      {
        headers: getDeviseHeaders(),
        credentials: 'same-origin',
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch copilot token: ${response.status}`);
    }

    const data = await response.json();
    cachedExpiresAt = data.expires_at;
    // Body token when Rails COPILOT_JWT_IN_BODY is enabled (required for local
    // cross-port LangGraph; cookie alone cannot cover localhost:3000 → :2024).
    cachedToken = data.token || null;
    if (!cachedToken) {
      // Cookie-only mode: still valid if LangGraph accepts the HttpOnly cookie.
      // Return empty string so callers skip Authorization but continue with credentials.
      cachedToken = '';
    }
    return cachedToken;
  }

  /**
   * Refresh the HttpOnly cookie (and optional body JWT) when near expiry.
   * Returns Bearer token string, or '' when cookie-only mode is active.
   */
  async function getToken() {
    const now = Math.floor(Date.now() / 1000);
    if (cachedExpiresAt > now + REFRESH_BUFFER_SECONDS && cachedToken !== null) {
      return cachedToken;
    }

    if (!inflight) {
      inflight = fetchSession().finally(() => {
        inflight = null;
      });
    }

    return inflight;
  }

  /** Alias — session is cookie-first; getToken refreshes it. */
  async function ensureSession() {
    await getToken();
  }

  function clearToken() {
    cachedToken = null;
    cachedExpiresAt = 0;
  }

  return { getToken, ensureSession, clearToken };
}

function getDeviseHeaders() {
  if (!Auth.hasAuthCookie()) return {};
  const {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  } = Auth.getAuthData();
  return {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  };
}

export function useCopilotToken(accountId) {
  const client = createCopilotTokenClient(accountId);
  return client;
}
